import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../../ui/button';
import { Progress } from '../../ui/progress';
import { formatCurrency, formatCurrencyDetailed } from './financingCalculations';
import { pctSymbol } from '../../../utils/formatters';

export default function RepaymentScheduleTable({ schedule, className = '' }) {
  const [expandedYears, setExpandedYears] = useState(new Set());
  const [showTable, setShowTable] = useState(false);

  const toggleYear = (year) => {
    const newExpanded = new Set(expandedYears);
    if (newExpanded.has(year)) {
      newExpanded.delete(year);
    } else {
      newExpanded.add(year);
    }
    setExpandedYears(newExpanded);
  };

  const expandAll = () => {
    setExpandedYears(new Set(schedule.map((y) => y.year)));
  };

  const collapseAll = () => {
    setExpandedYears(new Set());
  };

  if (!showTable) {
    return (
      <div className={`bg-succession-bg-primary border border-succession-border rounded-xl p-6 shadow-succession transition-colors duration-300 ${className}`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-succession-text-primary">Detailed Repayment Schedule</h3>
            <p className="text-sm text-succession-text-secondary mt-1">
              View year-by-year and month-by-month breakdown
            </p>
          </div>
          <Button onClick={() => setShowTable(true)} variant="outline">
            Show Schedule
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-succession-bg-primary border border-succession-border rounded-xl p-6 shadow-succession transition-colors duration-300 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-succession-text-primary">Detailed Repayment Schedule</h3>
          <p className="text-sm text-succession-text-secondary mt-1">Click on any year to see monthly breakdown</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={expandAll} variant="outline" size="sm">
            Expand All
          </Button>
          <Button onClick={collapseAll} variant="outline" size="sm">
            Collapse All
          </Button>
          <Button onClick={() => setShowTable(false)} variant="ghost" size="sm">
            Hide
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-succession-border bg-succession-bg-secondary">
              <th className="text-left py-3 px-4 font-semibold">Year</th>
              <th className="text-right py-3 px-4 font-semibold">Annual Interest</th>
              <th className="text-right py-3 px-4 font-semibold">Annual Principal</th>
              <th className="text-right py-3 px-4 font-semibold">Remaining Balance</th>
              <th className="text-right py-3 px-4 font-semibold">% Remaining</th>
            </tr>
          </thead>
          <tbody>
            {schedule.map((year) => {
              const isExpanded = expandedYears.has(year.year);

              return (
                <React.Fragment key={year.year}>
                  <tr
                    className="border-b border-succession-border hover:bg-succession-bg-secondary cursor-pointer transition-colors duration-300"
                    onClick={() => toggleYear(year.year)}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-succession-text-secondary" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-succession-text-secondary" />
                        )}
                        <span className="font-semibold">Year {year.year}</span>
                      </div>
                    </td>
                    <td className="text-right py-3 px-4 font-mono">
                      {formatCurrency(year.totalInterest)}
                    </td>
                    <td className="text-right py-3 px-4 font-mono">
                      {formatCurrency(year.totalPrincipal)}
                    </td>
                    <td className="text-right py-3 px-4 font-mono">
                      {formatCurrency(year.remainingBalance)}
                    </td>
                    <td className="text-right py-3 px-4">
                      <div className="flex items-center justify-end gap-3">
                        <Progress value={year.percentRemaining} className="w-20 h-2" />
                        <div className="font-mono text-xs text-succession-text-secondary w-12">
                          {year.percentRemaining.toFixed(1)}{pctSymbol()}
                        </div>
                      </div>
                    </td>
                  </tr>

                  {isExpanded && (
                    <React.Fragment>
                      <tr className="bg-succession-bg-secondary">
                        <td colSpan={5} className="py-2 px-4">
                          <div className="text-xs font-semibold text-succession-text-secondary uppercase tracking-wide">
                            Monthly Breakdown
                          </div>
                        </td>
                      </tr>
                      <tr className="bg-succession-bg-secondary text-xs">
                        <td className="py-2 px-8 text-succession-text-secondary">Month</td>
                        <td className="text-right py-2 px-4 text-succession-text-secondary">Interest</td>
                        <td className="text-right py-2 px-4 text-succession-text-secondary">Principal</td>
                        <td className="text-right py-2 px-4 text-succession-text-secondary">Balance</td>
                        <td className="text-right py-2 px-4 text-succession-text-secondary">Payment</td>
                      </tr>
                      {year.monthlyBreakdown.map((month) => (
                        <tr
                          key={month.month}
                          className="bg-succession-bg-primary border-b border-succession-border text-xs hover:bg-succession-bg-secondary transition-colors duration-300"
                        >
                          <td className="py-2 px-8 text-succession-text-secondary">Month {month.month}</td>
                          <td className="text-right py-2 px-4 font-mono">
                            {formatCurrencyDetailed(month.interest)}
                          </td>
                          <td className="text-right py-2 px-4 font-mono">
                            {formatCurrencyDetailed(month.principal)}
                          </td>
                          <td className="text-right py-2 px-4 font-mono">
                            {formatCurrency(month.remainingBalance)}
                          </td>
                          <td className="text-right py-2 px-4 font-mono">
                            {formatCurrencyDetailed(month.payment)}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
